import React, { useState, useEffect } from 'react';
import Modal from 'components/_layouts/Modal';
import { Box, Stack, Button, TextField, CircularProgress } from '@mui/material';
import { neutral } from '_lex/_foundations/phxColors';
import ContentEditor from 'components/ContentEditor';
import ActionMenu from 'components/_atoms/ActionMenu';
import CheckIcon from '@mui/icons-material/Check';
import { Edit } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';

interface GuideSheetEditModalProps {
  open: boolean;
  onClose: () => void;
  initialTitle?: string;
  initialStatus?: 'DRAFT' | 'APPROVED';
  initialContent?: string;
  onSave?: (data: { title: string; status: 'DRAFT' | 'APPROVED'; content: string }) => void;
  editorHeight?: number;
  onStatusChange?: (status: 'DRAFT' | 'APPROVED') => void;
  manualLevel?: string;
}

const statusOptions = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'APPROVED', label: 'Approved' },
];

const GuideSheetEditModal: React.FC<GuideSheetEditModalProps> = ({
  open,
  onClose,
  initialTitle = '',
  initialStatus = 'DRAFT',
  initialContent = '',
  onSave,
  editorHeight = 300,
  onStatusChange,
  manualLevel,
}) => {
  const [title, setTitle] = useState(initialTitle); // ISO string
  const [status, setStatus] = useState<'DRAFT' | 'APPROVED'>(initialStatus);
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setStatus(initialStatus);
      setContent(initialContent);
      setEditorResetKey((prev) => prev + 1);
    }
  }, [open, initialTitle, initialStatus, initialContent]);

  const isTitleEmpty = !title?.trim();

  const handleCancel = () => {
    // Reset all state to initial values
    setTitle(initialTitle);
    setStatus(initialStatus);
    setContent(initialContent);
    setSaving(false);
    setApproving(false);
    // Force SimpleEditor to reinitialize with original content
    setEditorResetKey((prev) => prev + 1);
    onClose();
  };

  const handleSave = async () => {
    if (isTitleEmpty) {
      enqueueSnackbar(t('draftManuals.guideSheet.titleRequired'), { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      if (onSave) {
        await onSave({ title, status, content });
        enqueueSnackbar(t('draftManuals.guideSheet.saveSuccess'), { variant: 'success' });
        onClose();
      }
    } catch (e) {
      let errorMessage = t('draftManuals.guideSheet.saveError');
      if (e && typeof e === 'object') {
        const errorObj = e as any;
        if (errorObj.httpStatus === 409) {
          errorMessage = t('draftManuals.guideSheet.saveErrorLocked');
        }
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={t('draftManuals.guideSheet.title')}
      subtitle={null}
      size='md'
      actions={
        manualLevel === 'AGENCY'
          ? []
          : [
              <Button
                key='cancel'
                variant='outlined'
                color='neutral'
                onClick={handleCancel}
                disabled={saving || approving}
                aria-label={t('draftManuals.guideSheet.cancelAria')}
              >
                {t('common.cancel')}
              </Button>,
              <Button
                key='save'
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={saving || approving || isTitleEmpty}
                endIcon={
                  saving || approving ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : null
                }
                aria-label={t('draftManuals.guideSheet.saveAria')}
              >
                {t('draftManuals.guideSheet.save')}
              </Button>,
            ]
      }
    >
      <Stack
        spacing={4}
        sx={{ mt: 2 }}
      >
        <Stack
          direction='row'
          spacing={3}
          alignItems='center'
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size='small'
              sx={{
                width: '100%',
                pt: 0.5,
                background: neutral[25],
                '& .MuiInputBase-root': { alignItems: 'center' },
              }}
              placeholder={t('draftManuals.guideSheet.titlePlaceholder')}
              disabled={manualLevel === 'AGENCY'}
              aria-label={t('draftManuals.guideSheet.titleAria')}
              fullWidth
            />
          </Box>
        </Stack>
        <Box sx={{ minHeight: editorHeight, maxHeight: editorHeight, height: editorHeight }}>
          {open && (
            <ContentEditor
              key={`guide-sheet-editor-${open}-${initialContent?.length || 0}-${editorResetKey}`}
              id='guide-sheet-editor'
              contentHTML={content || initialContent}
              onSave={setContent}
              isEditable={manualLevel !== 'AGENCY'}
              showToolbar={true}
              excalidrawEnabled={false}
              trackChangesEnabled={false}
              textEntitiesEnabled={false}
              crossReferencesEnabled={false}
              imagesEnabled={false}
              attachmentsEnabled={false}
              maxHeight='800px'
            />
          )}
        </Box>
      </Stack>
    </Modal>
  );
};

export default GuideSheetEditModal;
